/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable no-param-reassign */
import Vue from 'vue';
import { removeFirstVisibleCharacterInHtml } from './utils';
import {
    measureTextWithCanvas,
    resolveFontProperties,
} from '../utils/dom';

export const leftClipText = Vue.directive('leftClipText', {
    inserted(el: HTMLElement, binding: any) {
        updateTextClipping(el, unpackArguments(binding?.value));
    },

    componentUpdated(el: HTMLElement, binding: any) {
        updateTextClipping(el, unpackArguments(binding?.value));
    },
});

function updateTextClipping(el: HTMLElement, { text }: UnpackedArguments) {
    const fontProperties = resolveFontProperties(el);
    const elementAvailableWidth = el.clientWidth;

    let visibleText = text;

    // no need to clip, set text and return early
    if (visibleText.length > 0 && measureTextWithCanvas(visibleText, fontProperties) <= elementAvailableWidth) {
        el.innerHTML = visibleText;
        return;
    }

    // Clip and finally append ellipses
    while (visibleText.length > 0 && measureTextWithCanvas('…' + visibleText, fontProperties) > elementAvailableWidth) {
        visibleText = removeFirstVisibleCharacterInHtml(visibleText);
    }

    const expectedText = '…' + removeFirstVisibleCharacterInHtml(visibleText);

    el.innerHTML = expectedText;
}

function _getAvailableWidth(el: HTMLElement): number {
    const parentEl = el.parentElement;

    if (parentEl == null) {
        return document.body.clientWidth;
    }

    const parentPaddings = getNumericStyle(parentEl, 'padding-left') + getNumericStyle(parentEl, 'padding-right');
    const parentAvailableWidth = parentEl.clientWidth - parentPaddings;

    let availableWidth = parentAvailableWidth;

    const children = Array.from(parentEl.children) as HTMLElement[];
    const currentElementIndex = children.indexOf(el);

    availableWidth -= (children.length - 1) * getNumericStyle(parentEl, 'gap');

    children.forEach((child, index) => {
        if (child === el) {
            return;
        }

        if (isSkippedForWidthCalculations(child)) {
            return;
        }

        availableWidth -= child.clientWidth;

        if (index === 0) {
            availableWidth -= getNumericStyle(child, 'margin-left');

            if (currentElementIndex === 1) {
                availableWidth -= getNumericStyle(child, 'margin-right');
            }
        }

        if (index === children.length - 1) {
            availableWidth -= getNumericStyle(child, 'margin-right');
        }

        if (index > 0) {
            // Since margins collapse, take the larger of this one and the next one
            const previousChild = children[index - 1]!;
            availableWidth -= Math.max(
                getNumericStyle(previousChild, 'margin-right'),
                getNumericStyle(child, 'margin-left'),
            );
        }
    });

    return Math.max(0, availableWidth);
}

function unpackArguments(args: RawArguments): UnpackedArguments {
    if (args == null) {
        return { text: '' };
    } else {
        return {
            text: args ?? '',
        };
    }
}

function getNumericStyle(el: HTMLElement, prop: string): number {
    const styles = window.getComputedStyle(el, null);
    const value = styles.getPropertyValue(prop);
    const parsedValue = value ? parseInt(value.replace('px', ''), 10) : 0;

    return Number.isNaN(parsedValue) ? 0 : parsedValue;
}

function isSkippedForWidthCalculations(el: HTMLElement): boolean {
    const styles = window.getComputedStyle(el, null);
    return styles.getPropertyValue('display') === 'none'
        || styles.getPropertyValue('visibility') === 'hidden'
        || styles.getPropertyValue('position') === 'absolute'
        || styles.getPropertyValue('position') === 'fixed';
}

type RawArguments = string | undefined | null;

type UnpackedArguments = {
    text: string;
};
